import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactSelect from 'react-select';
import api from '../../api';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DocumentDuplicateIcon,
  CurrencyRupeeIcon,
  SparklesIcon,
  BellIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ProformaInvoice = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [form, setForm] = useState({
    quotationType: '',
    quotation: null,
    daysRequiredForDelivery: 1,
    items: [],
    paymentStatus: 'TOTAL_CREDIT',
    advanceAmount: 0,
    balanceAmount: 0,
    totalAmount: 0,
    subTotal: 0,
    discountAmount: 0,
    taxAmount: 0,
    cartage: 0,
    packingAndForwarding: 0,
    labour: 0,
    roundOff: 0,
    remarks: ''
  });

  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [itemDetailsModal, setItemDetailsModal] = useState({ open: false, item: null });
  const [quotationItemsModal, setQuotationItemsModal] = useState({ open: false, items: [] });
  const [quotationSelectionModal, setQuotationSelectionModal] = useState({ open: false, step: 1 });
  const [viewModal, setViewModal] = useState({ open: false, proformaInvoice: null });
  const [deliveryOrderModal, setDeliveryOrderModal] = useState({ 
    open: false, 
    proformaInvoiceId: null, 
    assignedOrders: [] 
  });
  const [completionModal, setCompletionModal] = useState({
    open: false,
    proformaInvoiceId: null,
    deliveryOrders: [],
    selectedLastDeliveryOrderId: null
  });
  const [reservedDetailsModal, setReservedDetailsModal] = useState({ open: false, item: null, details: [] });
  const [stockStatusModal, setStockStatusModal] = useState({ 
    open: false, 
    proformaInvoice: null, 
    selectedItems: [] 
  });
  const [quotationSearch, setQuotationSearch] = useState('');
  
  // Data states
  const [quotations, setQuotations] = useState([]);
  const [proformaInvoices, setProformaInvoices] = useState([]);
  const [availableDeliveryOrders, setAvailableDeliveryOrders] = useState([]);
  const [selectedDeliveryOrders, setSelectedDeliveryOrders] = useState([]);
  
  // Animation and Alert states
  const [isAnimating, setIsAnimating] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [itemAnimations, setItemAnimations] = useState({});
  const [buttonLoading, setButtonLoading] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    deliveryStatus: ''
  });

  // Stock status options
  const stockStatusOptions = [
    { value: 'to-be-reserved', label: 'To be Reserved' },
    { value: 'to-be-ordered', label: 'To Be Ordered' },
    { value: 'to-be-billed', label: 'To be Billed' }
  ];

  // Payment status options
  const paymentStatusOptions = [
    { value: 'TOTAL_CREDIT', label: 'Total Credit' },
    { value: 'ADVANCE_PARTIAL', label: 'Advance (Partial Payment)' },
    { value: 'FULL_PAID', label: 'Full Paid' }
  ];

  // Refs for keyboard navigation
  const formRef = useRef(null);
  const searchRef = useRef(null);
  const newButtonRef = useRef(null);
  const quotationTypeRef = useRef(null);
  const paymentStatusRef = useRef(null);
  const advanceAmountRef = useRef(null);

  // Keyboard shortcuts state
  const [keyboardHelp, setKeyboardHelp] = useState(false);

  // Animation and notification helper functions
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  const animateItem = (itemId, animationType = 'pulse') => {
    setItemAnimations(prev => ({ ...prev, [itemId]: animationType }));
    setTimeout(() => {
      setItemAnimations(prev => ({ ...prev, [itemId]: '' }));
    }, 1000);
  };

  const setButtonLoadingState = (buttonId, loading = true) => {
    setButtonLoading(prev => ({ ...prev, [buttonId]: loading }));
  };

  const showSuccessAlert = (title, message) => {
    Swal.fire({
      icon: 'success',
      title,
      text: message,
      confirmButtonColor: '#10B981',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      background: '#F0FDF4',
      color: '#065F46'
    });
  };

  const showErrorAlert = (title, message) => {
    Swal.fire({
      icon: 'error',
      title,
      text: message,
      confirmButtonColor: '#EF4444',
      background: '#FEF2F2',
      color: '#991B1B'
    });
  };

  const showWarningAlert = (title, message) => {
    return Swal.fire({
      icon: 'warning',
      title,
      text: message,
      showCancelButton: true,
      confirmButtonColor: '#F59E0B',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, proceed!',
      cancelButtonText: 'Cancel',
      background: '#FFFBEB',
      color: '#92400E'
    });
  };

  const showInfoAlert = (title, message) => {
    Swal.fire({
      icon: 'info',
      title,
      text: message,
      confirmButtonColor: '#3B82F6',
      background: '#EFF6FF',
      color: '#1E3A8A'
    });
  };

  useEffect(() => {
    fetchProformaInvoices();
    
    // Welcome animation
    setTimeout(() => {
      showNotification('Welcome to Proforma Invoice Management! 🎉', 'success');
    }, 1000);
  }, [page, filters]);

  // Auto-update advance amount when total amount changes and payment status requires it
  useEffect(() => {
    if (form.paymentStatus === 'FULL_PAID') {
      // For full paid, advance amount should always equal total amount
      if (form.advanceAmount !== form.totalAmount) {
        setForm(prev => ({
          ...prev,
          advanceAmount: prev.totalAmount,
          balanceAmount: 0
        }));
      }
    } else if (form.paymentStatus === 'TOTAL_CREDIT') {
      // For total credit, advance amount should always be 0
      if (form.advanceAmount !== 0) {
        setForm(prev => ({
          ...prev,
          advanceAmount: 0,
          balanceAmount: prev.totalAmount
        }));
      }
    } else {
      // For partial payment, just update balance amount
      const balanceAmount = Math.max(0, form.totalAmount - form.advanceAmount);
      if (form.balanceAmount !== balanceAmount) {
        setForm(prev => ({
          ...prev,
          balanceAmount
        }));
      }
    }
  }, [form.totalAmount, form.paymentStatus, form.advanceAmount, form.balanceAmount]);

  const fetchProformaInvoices = async () => {
    try {
      setLoading(true);
      setIsAnimating(true);
      
      const response = await api.get('/proforma-invoices', {
        params: {
          page,
          limit: 10,
          ...filters
        }
      });
      
      setProformaInvoices(response.data.proformaInvoices);
      setTotalPages(response.data.totalPages);
      
      // Animate each item as it loads
      response.data.proformaInvoices.forEach((invoice, index) => {
        setTimeout(() => {
          animateItem(invoice._id, 'slideIn');
        }, index * 100);
      });
      
      showNotification(`Loaded ${response.data.proformaInvoices.length} proforma invoices`, 'success');
    } catch (error) {
      console.error('Error fetching proforma invoices:', error);
      showErrorAlert('Error', 'Failed to load proforma invoices. Please try again.');
    } finally {
      setLoading(false);
      setIsAnimating(false);
    }
  };

  const fetchAvailableQuotations = async () => {
    try {
      setButtonLoadingState('quotation-fetch', true);
      showNotification('Fetching quotations...', 'info');
      
      const response = await api.get('/proforma-invoices/available-quotations', {
        params: {
          company: user.user.company._id || user.user.company
        }
      });
      
      setQuotations(response.data);
      showSuccessAlert('Success!', `Found ${response.data.length} available quotations`);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      showErrorAlert('Error', 'Failed to fetch quotations. Please try again.');
    } finally {
      setButtonLoadingState('quotation-fetch', false);
    }
  };

  const fetchQuotationItems = async (quotationId, type) => {
    try {
      const response = await api.get(`/proforma-invoices/quotation-items/${quotationId}`, {
        params: { type }
      });
      
      // Handle new response format - extract items from response
      const items = response.data.items || response.data;
      setQuotationItemsModal({ open: true, items });
    } catch (error) {
      console.error('Error fetching quotation items:', error);
    }
  };

  const fetchItemDetails = async (itemId, type) => {
    try {
      const response = await api.get(`/proforma-invoices/item-details/${itemId}`, {
        params: { type }
      });
      setItemDetailsModal({ open: true, item: response.data });
    } catch (error) {
      console.error('Error fetching item details:', error);
    }
  };

  const fetchReservedDetails = async (itemId, piId) => {
    try {
      console.log('Fetching reserved details for itemId:', itemId);
      const response = await api.get(`/proforma-invoices/reserved-details/${itemId}`, {
        params: { piId }
      });
      console.log('Reserved details response:', response.data);
      setReservedDetailsModal({ 
        open: true, 
        item: response.data.item, 
        details: response.data.reservedDetails || [] 
      });
    } catch (error) {
      console.error('Error fetching reserved details:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        showErrorAlert('Error', `Failed to fetch reserved details: ${error.response.data.message || error.message}`);
      } else {
        showErrorAlert('Error', 'Failed to fetch reserved details. Please try again.');
      }
    }
  };

  const handleQuotationTypeChange = (type) => {
    setForm({ ...form, quotationType: type, quotation: null, items: [] });
    fetchAvailableQuotations(type);
  };

  const handleQuotationSelect = (quotation) => {
    setForm({ 
      ...form, 
      quotation: quotation._id,
      quotationType: quotation.type 
    });
    // Auto-fetch and import items
    fetchAndImportQuotationItems(quotation._id, quotation.type);
    setQuotationSelectionModal({ open: false, step: 1 });
  };

  const fetchAndImportQuotationItems = async (quotationId, type) => {
    try {
      showNotification('Importing quotation items...', 'info');
      const response = await api.get(`/proforma-invoices/quotation-items/${quotationId}`, {
        params: { type }
      });
      
      console.log('Quotation items response:', response.data);
      
      // Handle new response format with items and additionalCharges
      const { items, additionalCharges } = response.data;
      
      if (!items || items.length === 0) {
        showErrorAlert('No Items', 'No items found in the selected quotation');
        return;
      }
      
      const processedItems = items.map(item => ({
        ...item,
        stockStatus: 'to-be-reserved', // default status
        reservedQuantity: item.reservedQuantity || 0
      }));
      
      // Update form with items and additional charges
      setForm(prev => ({ 
        ...prev, 
        items: processedItems,
        // Apply additional charges for quotation-new, reset for tally-quotation
        cartage: type === 'quotation-new' && additionalCharges ? (additionalCharges.cartage || 0) : 0,
        labour: type === 'quotation-new' && additionalCharges ? (additionalCharges.labour || 0) : 0,
        packingAndForwarding: type === 'quotation-new' && additionalCharges ? (additionalCharges.packingAndForwarding || 0) : 0,
        roundOff: type === 'quotation-new' && additionalCharges ? (additionalCharges.roundOff || 0) : 0,
        discountAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.specialDiscount || 0) : 0,
        taxAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.taxAmount || 0) : 0 // Reset taxAmount for tally quotations
      }));
      
      // Calculate totals with proper additional charges
      const chargesToUse = {
        cartage: type === 'quotation-new' && additionalCharges ? (additionalCharges.cartage || 0) : 0,
        labour: type === 'quotation-new' && additionalCharges ? (additionalCharges.labour || 0) : 0,
        packingAndForwarding: type === 'quotation-new' && additionalCharges ? (additionalCharges.packingAndForwarding || 0) : 0,
        roundOff: type === 'quotation-new' && additionalCharges ? (additionalCharges.roundOff || 0) : 0,
        discountAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.specialDiscount || 0) : 0,
        taxAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.taxAmount || 0) : 0 // Reset taxAmount for tally quotations
      };
      
      calculateTotals(processedItems, chargesToUse);
      showSuccessAlert('Success!', `Imported ${processedItems.length} items successfully! 🎉`);
    } catch (error) {
      console.error('Error fetching and importing quotation items:', error);
      showErrorAlert('Import Failed', 'Failed to import quotation items. Please try again.');
    }
  };

  const openQuotationSelectionModal = () => {
    setIsAnimating(true);
    showNotification('Opening quotation selection...', 'info');
    fetchAvailableQuotations(); // Fetch all quotations directly
    setQuotationSelectionModal({ open: true, step: 1 });
    
    setTimeout(() => {
      setIsAnimating(false);
      showInfoAlert('Select Quotation', 'Choose from available quotations! 📋');
    }, 500);
  };

  const handleModalQuotationTypeSelect = (type) => {
    setForm({ ...form, quotationType: type, quotation: null, items: [] });
    fetchAvailableQuotations(type);
    setQuotationSelectionModal({ open: true, step: 2 });
  };

  const handleItemsSelection = (selectedItems) => {
    const items = selectedItems.map(item => ({
      ...item,
      stockStatus: 'reserved' // default status
    }));
    setForm({ ...form, items });
    calculateTotals(items);
    setQuotationItemsModal({ open: false, items: [] });
  };

  const handleStockStatusChange = (index, stockStatus) => {
    const updatedItems = [...form.items];
    updatedItems[index].stockStatus = stockStatus;
    setForm({ ...form, items: updatedItems });
    
    // Animate the updated item
    animateItem(`item-${index}`, 'pulse');
    showNotification(`Stock status updated to: ${stockStatus}`, 'success');
  };

  const calculateTotals = (items, additionalChargesOverride = null) => {
    const subTotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Use override values if provided, otherwise use form values
    const charges = additionalChargesOverride || {
      cartage: form.cartage || 0,
      labour: form.labour || 0,
      packingAndForwarding: form.packingAndForwarding || 0,
      roundOff: form.roundOff || 0,
      discountAmount: form.discountAmount || 0,
      taxAmount: form.taxAmount || 0
    };
    
    const totalAmount = subTotal + charges.taxAmount + charges.cartage + 
                       charges.packingAndForwarding + charges.labour + charges.roundOff - charges.discountAmount;
    const balanceAmount = form.paymentStatus === 'PENDING' ? totalAmount : 
                         Math.max(0, totalAmount - (form.advanceAmount || 0));
    
    setForm(prev => ({
      ...prev,
      subTotal,
      totalAmount,
      balanceAmount,
      // Update the charges in form if override was provided
      ...(additionalChargesOverride ? {
        cartage: charges.cartage,
        labour: charges.labour,
        packingAndForwarding: charges.packingAndForwarding,
        roundOff: charges.roundOff,
        discountAmount: charges.discountAmount,
        taxAmount: charges.taxAmount
      } : {})
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setButtonLoadingState('submit', true);
      
      // Show loading animation
      Swal.fire({
        title: edit ? 'Updating Proforma Invoice...' : 'Creating Proforma Invoice...',
        text: 'Please wait while we process your request',
        icon: 'info',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      // Ensure required fields are included
      const formData = {
        ...form,
        createdBy: user.user._id,
        companyId: user.user.company
      };
      
      console.log('Submitting proforma invoice with data:', formData);
      console.log('User context:', user);
      
      if (edit) {
        await api.put(`/proforma-invoices/${editId}`, formData);
        showSuccessAlert('Success!', 'Proforma invoice updated successfully! 🎉');
      } else {
        await api.post('/proforma-invoices', formData);
        showSuccessAlert('Success!', 'Proforma invoice created successfully! 🎉');
      }
      
      setModalVisible(false);
      resetForm();
      fetchProformaInvoices();
      
      // Celebrate with confetti effect
      setTimeout(() => {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 2000);
      }, 500);
      
    } catch (error) {
      console.error('Error saving proforma invoice:', error);
      
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
      showErrorAlert('Error!', `Failed to ${edit ? 'update' : 'create'} proforma invoice: ${errorMessage}`);
      
      // Shake animation for error
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 1000);
    } finally {
      setLoading(false);
      setButtonLoadingState('submit', false);
    }
  };

  const handleComplete = async (id) => {
    try {
      const result = await showWarningAlert(
        'Complete Proforma Invoice',
        'Are you sure you want to mark this proforma invoice as complete? This action cannot be undone.'
      );
      
      if (result.isConfirmed) {
        setButtonLoadingState(`complete-${id}`, true);
        
        await api.patch(`/proforma-invoices/${id}/complete`);
        
        showSuccessAlert('Completed!', 'Proforma invoice marked as complete successfully! ✅');
        animateItem(id, 'bounce');
        fetchProformaInvoices();
      }
    } catch (error) {
      console.error('Error completing proforma invoice:', error);
      showErrorAlert('Error', 'Failed to complete proforma invoice. Please try again.');
    } finally {
      setButtonLoadingState(`complete-${id}`, false);
    }
  };

  const handleCompleteWithLastDeliveryOrder = async (proformaInvoiceId) => {
    try {
      // Fetch assigned delivery orders for this proforma invoice
      const response = await api.get(`/proforma-invoices/${proformaInvoiceId}/assigned-delivery-orders`, {
        params: { userId: user.user._id }
      });
      
      const assignedOrders = response.data || [];
      
      if (assignedOrders.length === 0) {
        showErrorAlert('Error', 'No delivery orders assigned to this proforma invoice. Please assign delivery orders first.');
        return;
      }
      
      if (assignedOrders.length === 1) {
        // If only one delivery order, use it as the last delivery order
        await completeProformaInvoice(proformaInvoiceId, assignedOrders[0]._id);
      } else {
        // If multiple delivery orders, show modal to select the last one
        setCompletionModal({
          open: true,
          proformaInvoiceId,
          deliveryOrders: assignedOrders,
          selectedLastDeliveryOrderId: null
        });
      }
    } catch (error) {
      console.error('Error fetching delivery orders for completion:', error);
      showErrorAlert('Error', 'Failed to fetch delivery orders. Please try again.');
    }
  };

  const completeProformaInvoice = async (proformaInvoiceId, lastDeliveryOrderId) => {
    try {
      const result = await showWarningAlert(
        'Complete Proforma Invoice',
        'Are you sure you want to mark this proforma invoice as complete? This action cannot be undone.'
      );
      
      if (result.isConfirmed) {
        setButtonLoadingState(`complete-${proformaInvoiceId}`, true);
        
        await api.patch(`/proforma-invoices/${proformaInvoiceId}/complete`, {
          lastDeliveryOrderId,
          completedBy: user.user._id
        }, {
          params: {
            userId: user.user._id
          }
        });
        
        showSuccessAlert('Completed!', 'Proforma invoice marked as complete successfully! ✅');
        animateItem(proformaInvoiceId, 'bounce');
        fetchProformaInvoices();
        
        // Close completion modal if open
        setCompletionModal({
          open: false,
          proformaInvoiceId: null,
          deliveryOrders: [],
          selectedLastDeliveryOrderId: null
        });
      }
    } catch (error) {
      console.error('Error completing proforma invoice:', error);
      showErrorAlert('Error', 'Failed to complete proforma invoice. Please try again.');
    } finally {
      setButtonLoadingState(`complete-${proformaInvoiceId}`, false);
    }
  };

  const handleEdit = (proformaInvoice) => {
    setIsAnimating(true);
    showNotification('Loading proforma invoice for editing...', 'info');
    
    setForm(proformaInvoice);
    setEdit(true);
    setEditId(proformaInvoice._id);
    
    // Pre-fetch quotations for the selected type
    if (proformaInvoice.quotationType) {
      fetchAvailableQuotations(proformaInvoice.quotationType);
    }
    
    setTabValue(1);
    animateItem(proformaInvoice._id, 'highlight');
    
    setTimeout(() => {
      setIsAnimating(false);
      showSuccessAlert('Ready to Edit!', 'Proforma invoice loaded for editing ✏️');
    }, 1000);
  };

  const handleView = (proformaInvoice) => {
    setViewModal({ open: true, proformaInvoice });
    showNotification('Opening proforma invoice details...', 'info');
  };

  const handlePrint = (proformaInvoice) => {
    // Open the ProformaInvoiceReview component in a new tab
    const printUrl = `/proforma-invoice-review/${proformaInvoice._id}`;
    window.open(printUrl, '_blank');
    showNotification('Opening print preview...', 'info');
  };

  const handleUpdateStockStatus = (proformaInvoice) => {
    // Find items that can be updated (status = 'to-be-reserved')
    const eligibleItems = proformaInvoice.items.filter(item => item.stockStatus === 'to-be-reserved');
    
    if (eligibleItems.length === 0) {
      showErrorAlert('No Items to Update', 'There are no items with "To be Reserved" status to update.');
      return;
    }

    setStockStatusModal({ 
      open: true, 
      proformaInvoice, 
      selectedItems: [...eligibleItems.map(item => item._id)] // Pre-select all eligible items
    });
  };

  const handleConfirmStockStatusUpdate = async () => {
    const { proformaInvoice, selectedItems } = stockStatusModal;
    
    if (selectedItems.length === 0) {
      showErrorAlert('No Items Selected', 'Please select at least one item to update.');
      return;
    }

    try {
      setButtonLoadingState(`update-stock-${proformaInvoice._id}`, true);
      
      const response = await api.patch(`/proforma-invoices/${proformaInvoice._id}/update-stock-status`, {
        itemIds: selectedItems
      }, {
        params: {
          userId: user.user._id
        }
      });
      
      showSuccessAlert(
        'Stock Status Updated!', 
        `Successfully updated ${response.data.updatedItemsCount} items to "To Be Billed" status ✅`
      );
      
      setStockStatusModal({ open: false, proformaInvoice: null, selectedItems: [] });
      fetchProformaInvoices(); // Refresh the list
    } catch (error) {
      console.error('Error updating stock status:', error);
      showErrorAlert('Error', 'Failed to update stock status. Please try again.');
    } finally {
      setButtonLoadingState(`update-stock-${proformaInvoice._id}`, false);
    }
  };

  const fetchAvailableDeliveryOrders = async (proformaInvoiceId) => {
    try {
      showNotification('Loading delivery orders...', 'info');
      
      // Fetch both available and already assigned delivery orders
      const [availableResponse, assignedResponse] = await Promise.all([
        api.get(`/proforma-invoices/${proformaInvoiceId}/available-delivery-orders`, {
          params: { userId: user.user._id }
        }),
        api.get(`/proforma-invoices/${proformaInvoiceId}/assigned-delivery-orders`, {
          params: { userId: user.user._id }
        })
      ]);
      
      setAvailableDeliveryOrders(availableResponse.data);
      setSelectedDeliveryOrders([]); // Reset selection
      
      // Set the modal with both available and assigned orders
      setDeliveryOrderModal({ 
        open: true, 
        proformaInvoiceId,
        assignedOrders: assignedResponse.data || []
      });
      
      showNotification(`Found ${availableResponse.data.length} available delivery orders`, 'success');
    } catch (error) {
      console.error('Error fetching available delivery orders:', error);
      showErrorAlert('Error', 'Failed to fetch available delivery orders');
    }
  };

  const handleAssignDeliveryOrders = async () => {
    try {
      if (selectedDeliveryOrders.length === 0) {
        showErrorAlert('Error', 'Please select at least one delivery order');
        return;
      }

      setButtonLoadingState('assign-delivery-orders', true);
      
      const response = await api.post(`/proforma-invoices/${deliveryOrderModal.proformaInvoiceId}/assign-delivery-orders`, {
        deliveryOrderIds: selectedDeliveryOrders,
        userId: user.user._id
      });

      // Refresh the assigned orders list
      const assignedResponse = await api.get(`/proforma-invoices/${deliveryOrderModal.proformaInvoiceId}/assigned-delivery-orders`);
      
      setDeliveryOrderModal(prev => ({
        ...prev,
        assignedOrders: assignedResponse.data || []
      }));
      
      setSelectedDeliveryOrders([]);
      fetchProformaInvoices(); // Refresh the main list
      
      showSuccessAlert(
        'Success!', 
        `${selectedDeliveryOrders.length} delivery order(s) assigned successfully! Status updated to ${response.data.status}`
      );
      
      // Refresh available orders after assignment
      fetchAvailableDeliveryOrders(deliveryOrderModal.proformaInvoiceId);
    } catch (error) {
      console.error('Error assigning delivery orders:', error);
      showErrorAlert('Error', 'Failed to assign delivery orders');
    } finally {
      setButtonLoadingState('assign-delivery-orders', false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This proforma invoice will be permanently deleted!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#EF4444',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'animate-pulse'
        }
      });

      if (result.isConfirmed) {
        setButtonLoadingState(`delete-${id}`, true);
        
        // Show deletion progress
        Swal.fire({
          title: 'Deleting...',
          text: 'Please wait while we delete the proforma invoice',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        await api.delete(`/proforma-invoices/${id}`);
        
        // Animate item removal
        animateItem(id, 'fadeOut');
        
        setTimeout(() => {
          fetchProformaInvoices();
          showSuccessAlert('Deleted!', 'Proforma invoice has been deleted successfully! 🗑️');
        }, 1000);
      }
    } catch (error) {
      console.error('Error deleting proforma invoice:', error);
      showErrorAlert('Error', 'Failed to delete proforma invoice. Please try again.');
    } finally {
      setButtonLoadingState(`delete-${id}`, false);
    }
  };

  const resetForm = () => {
    setForm({
      quotationType: '',
      quotation: null,
      daysRequiredForDelivery: 1,
      items: [],
      paymentStatus: 'TOTAL_CREDIT',
      advanceAmount: 0,
      balanceAmount: 0,
      totalAmount: 0,
      subTotal: 0,
      discountAmount: 0,
      taxAmount: 0,
      cartage: 0,
      packingAndForwarding: 0,
      labour: 0,
      roundOff: 0,
      remarks: ''
    });
    setEdit(false);
    setEditId(null);
    setQuotationSelectionModal({ open: false, step: 1 });
    setDeliveryOrderModal({ open: false, proformaInvoiceId: null, assignedOrders: [] });
    setQuotations([]);
    setSelectedDeliveryOrders([]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'PARTIAL': return 'info';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'PARTIAL': return 'info';
      case 'PAID': return 'success';
      default: return 'default';
    }
  };

  const handlePaymentStatusChange = (paymentStatus) => {
    let advanceAmount = 0;
    
    if (paymentStatus === 'FULL_PAID') {
      // Full paid: advance amount = total amount (disabled)
      advanceAmount = form.totalAmount;
    } else if (paymentStatus === 'TOTAL_CREDIT') {
      // Total credit: advance amount = 0 (disabled)
      advanceAmount = 0;
    } else if (paymentStatus === 'ADVANCE_PARTIAL') {
      // Partial payment: keep current advance amount or set reasonable default (editable)
      advanceAmount = form.advanceAmount || 0;
    }
    
    const balanceAmount = Math.max(0, form.totalAmount - advanceAmount);
    
    setForm(prev => ({
      ...prev,
      paymentStatus,
      advanceAmount,
      balanceAmount
    }));
  };

  // Keyboard event handler
  const handleKeyDown = useCallback((e) => {
    // Global shortcuts (work anywhere)
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'n':
          e.preventDefault();
          if (!modalVisible) {
            setTabValue(1);
            setTimeout(() => quotationTypeRef.current?.focus(), 100);
          }
          break;
        case 's':
          e.preventDefault();
          if (modalVisible && (form.quotation && form.items.length > 0)) {
            handleSubmit();
          }
          break;
        case 'Escape':
        case 'w':
          e.preventDefault();
          if (modalVisible) {
            setTabValue(0);
            resetForm();
          }
          break;
        case '/':
          e.preventDefault();
          searchRef.current?.focus();
          break;
        case '?':
          e.preventDefault();
          setKeyboardHelp(!keyboardHelp);
          break;
      }
    }

    // Form navigation (Tab/Shift+Tab for better UX)
    if (modalVisible && e.key === 'Tab') {
      // Let browser handle normal tab navigation
      return;
    }

    // Pagination shortcuts
    if (!modalVisible && !keyboardHelp) {
      if (e.key === 'ArrowLeft' && page > 1) {
        e.preventDefault();
        setPage(prev => prev - 1);
      }
      if (e.key === 'ArrowRight' && page < totalPages) {
        e.preventDefault();
        setPage(prev => prev + 1);
      }
    }

    // Close modals with Escape
    if (e.key === 'Escape') {
      if (keyboardHelp) {
        setKeyboardHelp(false);
      } else if (itemDetailsModal.open) {
        setItemDetailsModal({ open: false, item: null });
      } else if (quotationItemsModal.open) {
        setQuotationItemsModal({ open: false, items: [] });
      } else if (quotationSelectionModal.open) {
        setQuotationSelectionModal({ open: false, step: 1 });
      }
    }
  }, [modalVisible, form, page, totalPages, keyboardHelp, itemDetailsModal, quotationItemsModal, quotationSelectionModal]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header with enhanced styling */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Proforma Invoice Management
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Create and manage proforma invoices efficiently
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setKeyboardHelp(true)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Keyboard shortcuts (Ctrl+?)"
              >
                <span className="text-sm">Ctrl+?</span>
              </button>
              <button
                ref={newButtonRef}
                onClick={() => {
                  setTabValue(1);
                  setIsAnimating(true);
                  showNotification('Opening new proforma invoice form...', 'info');
                  setTimeout(() => {
                    quotationTypeRef.current?.focus();
                    setIsAnimating(false);
                    showSuccessAlert('Ready to Create!', 'Fill in the details to create a new proforma invoice ✨');
                  }, 1000);
                }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl animate-pulse"
                title="Create new (Ctrl+N)"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Invoice
                <SparklesIcon className="h-4 w-4 ml-2" />
                <span className="ml-2 text-xs opacity-75">Ctrl+N</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Enhanced Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1">
            <nav className="flex space-x-1">
              <button
                onClick={() => setTabValue(0)}
                className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 ${
                  tabValue === 0
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
                  Proforma Invoices
                </div>
              </button>
              <button
                onClick={() => setTabValue(1)}
                className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 ${
                  tabValue === 1
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create New
                </div>
              </button>
            </nav>
          </div>
        </div>

        {tabValue === 0 && (
        <div>
          {/* Enhanced Filters */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filters & Search</h3>
              <span className="text-sm text-gray-500">Use arrow keys ← → for pagination</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search invoices... (Ctrl+/)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Status
                </label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  <option value="">All Payments</option>
                  <option value="PENDING">Pending</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="PAID">Paid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Status
                </label>
                <select
                  value={filters.deliveryStatus}
                  onChange={(e) => setFilters({ ...filters, deliveryStatus: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  <option value="">All Deliveries</option>
                  <option value="PENDING">Pending</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Actions
                </label>
                <button
                  onClick={() => setFilters({ status: '', paymentStatus: '', deliveryStatus: '' })}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Enhanced Table */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Proforma Invoices</h3>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Previous page (←)"
                    >
                      <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Next page (→)"
                    >
                      <ArrowRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center">
                        <span className="mr-2">#</span>
                        Invoice Number
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Quotation Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center">
                        <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
                        Total Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Payment Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Delivery Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Expected Delivery
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
                          <p className="mt-4 text-gray-600">Loading invoices...</p>
                        </div>
                      </td>
                    </tr>
                  ) : proformaInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                      No proforma invoices found
                    </td>
                  </tr>
                ) : (
                  proformaInvoices.map((invoice) => (
                    <tr 
                      key={invoice._id} 
                      className={`hover:bg-gray-50 transition-all duration-300 transform hover:scale-[1.01] ${
                        itemAnimations[invoice._id] ? `animate-${itemAnimations[invoice._id]}` : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {invoice.quotationType === 'quotation-new' ? 'Quotation (new)' : 'Tally Quotation'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          getPaymentStatusColor(invoice.paymentStatus)
                        }`}>
                          {invoice.paymentStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          getStatusColor(invoice.status)
                        }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          invoice.deliveryStatus === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          invoice.deliveryStatus === 'PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.deliveryStatus || 'PENDING'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(invoice.expectedDeliveryDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleView(invoice)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          
                          {/* Print Button */}
                          <button
                            onClick={() => handlePrint(invoice)}
                            className="text-purple-600 hover:text-purple-900 transition-colors"
                            title="Print Invoice"
                          >
                            <svg 
                              className="h-5 w-5" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" 
                              />
                            </svg>
                          </button>
                          
                          {/* Update Stock Status Button - Show for PENDING or PARTIAL invoices */}
                          {(invoice.status === 'PENDING' || invoice.status === 'PARTIAL') && (
                            <button
                              onClick={() => handleUpdateStockStatus(invoice)}
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded transition-colors ${
                                buttonLoading[`update-stock-${invoice._id}`] ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title="Update Stock Status (To be Reserved → To Be Billed)"
                              disabled={buttonLoading[`update-stock-${invoice._id}`]}
                            >
                              {buttonLoading[`update-stock-${invoice._id}`] ? (
                                <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-1"></div>
                              ) : (
                                <SparklesIcon className="h-3 w-3 mr-1" />
                              )}
                              Update Stock
                            </button>
                          )}

                          {(invoice.status === 'PENDING' || invoice.status === 'PARTIAL') && (
                            <>
                              <button
                                onClick={() => fetchAvailableDeliveryOrders(invoice._id)}
                                className="text-green-600 hover:text-green-900 transition-colors mr-2"
                                title="Assign Delivery Orders"
                              >
                                <DocumentDuplicateIcon className="h-5 w-5" />
                              </button>
                              {(invoice.deliveryStatus === 'PARTIAL' || 
                                (invoice.deliveryOrders && invoice.deliveryOrders.length > 0)) && (
                                <button
                                  onClick={() => handleCompleteWithLastDeliveryOrder(invoice._id)}
                                  className={`text-purple-600 hover:text-purple-900 transition-all duration-200 transform hover:scale-110 ${
                                    buttonLoading[`complete-${invoice._id}`] ? 'button-loading' : ''
                                  }`}
                                  title="Complete"
                                  disabled={buttonLoading[`complete-${invoice._id}`]}
                                >
                                  {buttonLoading[`complete-${invoice._id}`] ? (
                                    <div className="h-5 w-5"></div>
                                  ) : (
                                    <CheckCircleIcon className="h-5 w-5" />
                                  )}
                                </button>
                              )}
                            </>
                          )}
                          {(invoice.status !== 'PENDING' && invoice.status !== 'PARTIAL') && (
                            <span className="text-gray-400 text-sm">No actions available</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    page === pageNum
                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  } ${pageNum === 1 ? 'rounded-l-md' : ''} ${pageNum === totalPages ? 'rounded-r-md' : ''}`}
                >
                  {pageNum}
                </button>
              ))}
            </nav>
          </div>
          </div>
        </div>
      )}

      {tabValue === 1 && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Create New Proforma Invoice</h2>
                <p className="text-gray-600 mt-1">Fill in the details below to create a new proforma invoice</p>
              </div>
              <div className="text-sm text-gray-500 space-y-1">
                <div>Save: <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Ctrl+S</kbd></div>
                <div>Cancel: <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Ctrl+W</kbd></div>
              </div>
            </div>
          </div>
          
          <form 
            ref={formRef} 
            className="p-8 space-y-8"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {/* Step 1: Enhanced Quotation Selection */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 transition-colors">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  1
                </div>
                <h3 className="ml-3 text-lg font-semibold text-gray-900">
                  Select Quotation
                </h3>
              </div>
              
              {!form.quotation ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-6">
                    <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No quotation selected</h4>
                  <p className="text-gray-500 mb-6">Choose a quotation to get started with your proforma invoice</p>
                  <p className="text-gray-500 mb-4">
                    No quotation selected. Please select a quotation to proceed.
                  </p>
                  <button
                    type="button"
                    onClick={openQuotationSelectionModal}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Select Quotation
                  </button>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Quotation Selected
                      </p>
                      <p className="text-sm text-green-600">
                        Type: {form.quotationType === 'quotation-new' ? 'Quotation (new)' : 'Tally Quotation'}
                      </p>
                      <p className="text-sm text-green-600">
                        Items: {form.items.length} item(s) imported
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openQuotationSelectionModal}
                      className="text-green-600 hover:text-green-800"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Show rest of the form only if quotation is selected */}
            {form.quotation && (
              <>
                {/* Step 2: Enhanced Delivery & Payment Details */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-dashed border-orange-300 rounded-xl p-6 hover:border-orange-400 transition-colors">
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      2
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">
                      Delivery & Payment Details
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Days Required for Delivery */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-2">
                            <span className="text-orange-600 text-xs font-semibold">📅</span>
                          </div>
                          Days Required for Delivery
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="1"
                          value={form.daysRequiredForDelivery}
                          onChange={(e) => setForm({ ...form, daysRequiredForDelivery: parseInt(e.target.value) })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-center text-lg font-semibold"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">days</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Status */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                            <CurrencyRupeeIcon className="h-3 w-3 text-green-600" />
                          </div>
                          Payment Status
                        </div>
                      </label>
                      <div className="relative">
                        <select
                          value={form.paymentStatus}
                          onChange={(e) => handlePaymentStatusChange(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-center text-lg font-semibold appearance-none bg-white"
                        >
                          {paymentStatusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      <div className="mt-2 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          form.paymentStatus === 'FULL_PAID' ? 'bg-green-100 text-green-800' :
                          form.paymentStatus === 'ADVANCE_PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {form.paymentStatus === 'FULL_PAID' ? '✅ Fully Paid' :
                           form.paymentStatus === 'ADVANCE_PARTIAL' ? '⚠️ Partial Payment' :
                           '❌ Total Credit'}
                        </span>
                      </div>
                    </div>

                    {/* Advance Amount - Always show but with conditional styling */}
                    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${
                      form.paymentStatus === 'TOTAL_CREDIT' || form.paymentStatus === 'FULL_PAID' ? 'opacity-75' : ''
                    }`}>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                            <CurrencyRupeeIcon className="h-3 w-3 text-blue-600" />
                          </div>
                          Advance Amount
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={form.advanceAmount}
                          onChange={(e) => {
                            const advanceAmount = parseFloat(e.target.value) || 0;
                            const balanceAmount = Math.max(0, form.totalAmount - advanceAmount);
                            setForm({ ...form, advanceAmount, balanceAmount });
                          }}
                          disabled={form.paymentStatus === 'TOTAL_CREDIT' || form.paymentStatus === 'FULL_PAID'}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-lg font-semibold disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                          placeholder="0.00"
                          min="0"
                          max={form.totalAmount}
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyRupeeIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                      {form.paymentStatus === 'TOTAL_CREDIT' && (
                        <div className="mt-2 text-center">
                          <span className="text-xs text-red-600 font-medium">
                            ❌ Auto-set to ₹0 (Total Credit)
                          </span>
                        </div>
                      )}
                      {form.paymentStatus === 'FULL_PAID' && (
                        <div className="mt-2 text-center">
                          <span className="text-xs text-green-600 font-medium">
                            ✅ Auto-set to {formatCurrency(form.totalAmount)} (Full Paid)
                          </span>
                        </div>
                      )}
                      {form.paymentStatus === 'ADVANCE_PARTIAL' && (
                        <div className="mt-2 text-center">
                          <span className="text-xs text-blue-600 font-medium">
                            💰 Enter partial payment amount
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 3: Enhanced Imported Items */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-dashed border-blue-300 rounded-xl p-6 hover:border-blue-400 transition-colors">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        3
                      </div>
                      <h3 className="ml-3 text-lg font-semibold text-gray-900">
                        Imported Items
                      </h3>
                      <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {form.items.length} items
                      </span>
                    </div>
                    {form.items.length > 0 && (
                      <div className="text-sm text-gray-600">
                        Total: <span className="font-semibold text-gray-900">{formatCurrency(form.subTotal)}</span>
                      </div>
                    )}
                  </div>
                  
                  {form.items.length > 0 ? (
                    <div className="space-y-4">
                      {form.items.map((item, index) => (
                        <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                          <div className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start mb-3">
                                  <div className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-medium text-xs mr-3 mt-1">
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-lg font-medium text-gray-900 break-words leading-relaxed">
                                      {item.itemName || item.Name || item.skuDescription}
                                    </h4>
                                    {item.itemDescription && (
                                      <p className="text-sm text-gray-600 mt-1 break-words leading-relaxed">
                                        {item.itemDescription}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                      Brand
                                    </div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {item.brandName || 'N/A'}
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                      {item.calculationType === 'pcsPerBox' ? 'Boxes & Pieces' : 'Quantity'}
                                    </div>
                                    <div className="text-sm font-medium text-gray-900 flex items-center">
                                      {item.calculationType === 'pcsPerBox' ? (
                                        <div className="flex items-center space-x-2">
                                          <span className="text-lg font-semibold text-blue-600">{item.boxes || 0}</span>
                                          <span className="text-gray-500">boxes</span>
                                          <span className="text-gray-400">+</span>
                                          <span className="text-lg font-semibold text-blue-600">{item.pieces || 0}</span>
                                          <span className="text-gray-500">pcs</span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center">
                                          <span className="text-lg font-semibold text-blue-600">{item.quantity}</span>
                                          <span className="ml-1 text-gray-500">{item.unit || 'units'}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                      Amount
                                    </div>
                                    <div className="text-sm font-semibold text-green-600 flex items-center">
                                      <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
                                      {formatCurrency(item.amount || 0).replace('₹', '')}
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                      Reserved
                                    </div>
                                    <div className="text-sm font-medium text-gray-900 flex items-center justify-between">
                                      <div className="flex items-center">
                                        <span className="text-lg font-semibold text-orange-600">{item.reservedQuantity || 0}</span>
                                        <span className="ml-1 text-gray-500">{item.unit || 'units'}</span>
                                        {(item.reservedQuantity > 0) && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              fetchReservedDetails(item.item || item._id, null);
                                            }}
                                            className="text-purple-600 hover:text-purple-800 ml-2 p-1 rounded hover:bg-purple-50 transition-colors"
                                            title="View Reserved Details"
                                          >
                                            <InformationCircleIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                      Stock Status
                                    </div>
                                    <ReactSelect
                                      value={stockStatusOptions.find(opt => opt.value === item.stockStatus)}
                                      options={stockStatusOptions}
                                      onChange={(selectedOption) => handleStockStatusChange(index, selectedOption.value)}
                                      className="react-select-container"
                                      classNamePrefix="react-select"
                                      styles={{
                                        control: (base, state) => ({
                                          ...base,
                                          minHeight: '36px',
                                          height: '36px',
                                          fontSize: '14px',
                                          border: '2px solid #e5e7eb',
                                          borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
                                          borderRadius: '8px',
                                          boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                                          '&:hover': {
                                            borderColor: '#3b82f6'
                                          }
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          height: '36px',
                                          padding: '0 12px'
                                        }),
                                        input: (base) => ({
                                          ...base,
                                          margin: '0px'
                                        }),
                                        indicatorSeparator: () => ({
                                          display: 'none'
                                        }),
                                        indicatorsContainer: (base) => ({
                                          ...base,
                                          height: '36px'
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          zIndex: 9999,
                                          borderRadius: '8px',
                                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                                        }),
                                        option: (base, state) => ({
                                          ...base,
                                          backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
                                          color: state.isSelected ? 'white' : '#374151',
                                          '&:hover': {
                                            backgroundColor: state.isSelected ? '#3b82f6' : '#eff6ff'
                                          }
                                        })
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <DocumentDuplicateIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Yet</h3>
                      <p className="text-gray-500 mb-4">
                        Select a quotation to import items automatically
                      </p>
                      <button
                        type="button"
                        onClick={openQuotationSelectionModal}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Select Quotation
                      </button>
                    </div>
                  )}
                </div>

                {/* Step 4: Enhanced Additional Charges */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-dashed border-green-300 rounded-xl p-6 hover:border-green-400 transition-colors">
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      4
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">
                      Additional Charges
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Cartage
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.cartage}
                        onChange={(e) => {
                          const cartage = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, cartage }));
                          // Calculate totals with the new cartage value immediately
                          calculateTotals(form.items, {
                            cartage,
                            labour: form.labour || 0,
                            packingAndForwarding: form.packingAndForwarding || 0,
                            roundOff: form.roundOff || 0,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Packing & Forwarding
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.packingAndForwarding}
                        onChange={(e) => {
                          const packingAndForwarding = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, packingAndForwarding }));
                          // Calculate totals with the new packing value immediately
                          calculateTotals(form.items, {
                            cartage: form.cartage || 0,
                            labour: form.labour || 0,
                            packingAndForwarding,
                            roundOff: form.roundOff || 0,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Labour
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.labour}
                        onChange={(e) => {
                          const labour = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, labour }));
                          // Calculate totals with the new labour value immediately
                          calculateTotals(form.items, {
                            cartage: form.cartage || 0,
                            labour,
                            packingAndForwarding: form.packingAndForwarding || 0,
                            roundOff: form.roundOff || 0,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Round Off
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.roundOff}
                        onChange={(e) => {
                          const roundOff = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, roundOff }));
                          // Calculate totals with the new round off value immediately
                          calculateTotals(form.items, {
                            cartage: form.cartage || 0,
                            labour: form.labour || 0,
                            packingAndForwarding: form.packingAndForwarding || 0,
                            roundOff,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Step 5: Enhanced Total Summary */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-dashed border-purple-300 rounded-xl p-6 hover:border-purple-400 transition-colors">
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      5
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">
                      Total Summary
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600">Sub Total</p>
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-blue-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(form.subTotal)}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600">Total Amount</p>
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-green-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(form.totalAmount)}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600">Advance Amount</p>
                        <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-yellow-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-yellow-600">
                        {formatCurrency(form.advanceAmount)}
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-600">Balance Amount</p>
                        <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-red-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-red-600">
                        {formatCurrency(form.balanceAmount)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 6: Enhanced Remarks */}
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-gray-400 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      6
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">
                      Remarks
                    </h3>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <textarea
                      rows={4}
                      value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors resize-none"
                      placeholder="Add any additional remarks or notes for this proforma invoice..."
                    />
                  </div>
                </div>

                {/* Enhanced Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setTabValue(0);
                      resetForm();
                    }}
                    className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!form.quotation || form.items.length === 0 || buttonLoading.submit}
                    className={`inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                      buttonLoading.submit ? 'button-loading' : ''
                    }`}
                  >
                    {buttonLoading.submit ? (
                      <>
                        <div className="h-5 w-5 mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-5 w-5 mr-2" />
                        {edit ? 'Update' : 'Save'} Proforma Invoice
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}

      {/* Quotation Selection Modal */}
      {quotationSelectionModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Select Quotation
                </h3>
                <button
                  onClick={() => {
                    setQuotationSelectionModal({ open: false, step: 1 });
                    setQuotationSearch('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search quotations by ID, recipient, or amount..."
                    value={quotationSearch}
                    onChange={(e) => setQuotationSearch(e.target.value)}
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {quotations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quotation ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {quotations
                        .filter(quotation => {
                          if (!quotationSearch) return true;
                          const searchLower = quotationSearch.toLowerCase();
                          return (
                            (quotation.quotationNumber || '').toLowerCase().includes(searchLower) ||
                            (quotation.id || '').toLowerCase().includes(searchLower) ||
                            (quotation.recipientName || '').toLowerCase().includes(searchLower) ||
                            (quotation.totalAmount || '').toString().includes(searchLower) ||
                            (quotation.typeLabel || '').toLowerCase().includes(searchLower)
                          );
                        })
                        .map((quotation) => (
                        <tr key={`${quotation._id}_${quotation.type}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              quotation.type === 'quotation-new' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {quotation.typeLabel}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {quotation.quotationNumber || quotation.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.recipientName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            {formatCurrency(quotation.totalAmount || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.createdAt ? new Date(quotation.createdAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleQuotationSelect(quotation)}
                              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium transition-colors shadow-sm hover:shadow-md"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Show filtered count */}
                  <div className="mt-4 text-sm text-gray-600 text-center">
                    Showing {quotations.filter(quotation => {
                      if (!quotationSearch) return true;
                      const searchLower = quotationSearch.toLowerCase();
                      return (
                        (quotation.quotationNumber || '').toLowerCase().includes(searchLower) ||
                        (quotation.id || '').toLowerCase().includes(searchLower) ||
                        (quotation.recipientName || '').toLowerCase().includes(searchLower) ||
                        (quotation.totalAmount || '').toString().includes(searchLower) ||
                        (quotation.typeLabel || '').toLowerCase().includes(searchLower)
                      );
                    }).length} of {quotations.length} quotations
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 mb-4">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No quotations available</h3>
                  <p className="text-gray-500">
                    There are no approved quotations available for selection.
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setQuotationSelectionModal({ open: false, step: 1 });
                    setQuotationSearch('');
                  }}
                  className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quotation Items Modal */}
      {quotationItemsModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Select Items for Proforma Invoice
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Brand
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reserved Qty
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stock Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quotationItemsModal.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 text-sm text-gray-900 min-w-0">
                          <div className="break-words leading-relaxed">
                            {item.itemName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.brandName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            item.reservedQuantity > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.reservedQuantity || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ReactSelect
                            value={stockStatusOptions.find(opt => opt.value === item.stockStatus) || stockStatusOptions[0]}
                            options={stockStatusOptions}
                            onChange={(selectedOption) => {
                              const updatedItems = [...quotationItemsModal.items];
                              updatedItems[index].stockStatus = selectedOption.value;
                              setQuotationItemsModal({ ...quotationItemsModal, items: updatedItems });
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              fetchItemDetails(item._id, item.itemType);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <InformationCircleIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setQuotationItemsModal({ open: false, items: [] })}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleItemsSelection(quotationItemsModal.items)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Select Items
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Details Modal */}
      {itemDetailsModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Item Details
              </h3>
              {itemDetailsModal.item && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Name</h4>
                    <p className="text-sm text-gray-900">
                      {itemDetailsModal.item.Name || itemDetailsModal.item.skuDescription}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Brand</h4>
                    <p className="text-sm text-gray-900">
                      {itemDetailsModal.item.brand?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Category</h4>
                    <p className="text-sm text-gray-900">
                      {itemDetailsModal.item.assigncategory?.name || itemDetailsModal.item.category?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Stock Balance</h4>
                    <p className="text-sm text-gray-900">
                      {itemDetailsModal.item.closingBalance || 'N/A'}
                    </p>
                  </div>
                  {itemDetailsModal.item.parameter && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Calculation Type</h4>
                      <p className="text-sm text-gray-900">
                        {itemDetailsModal.item.parameter.calculationType}
                      </p>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setItemDetailsModal({ open: false, item: null })}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reserved Details Modal */}
      {reservedDetailsModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Reserved Details - {reservedDetailsModal.item?.itemName || 'Item'}
              </h3>
              {reservedDetailsModal.details.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          PI ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reserved Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date Reserved
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reservedDetailsModal.details.map((detail, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {detail.recipient?.name || detail.recipientName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {detail.piId || detail.proformaInvoiceId || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="font-semibold text-orange-600">
                              {detail.reservedQuantity} {detail.unit || 'units'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {detail.dateReserved ? new Date(detail.dateReserved).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              detail.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                              detail.status === 'FULFILLED' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {detail.status || 'ACTIVE'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <InformationCircleIcon className="mx-auto h-12 w-12" />
                  </div>
                  <p className="text-gray-500">No reserved details found for this item.</p>
                </div>
              )}
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setReservedDetailsModal({ open: false, item: null, details: [] })}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Proforma Invoice Modal */}
      {viewModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Proforma Invoice Details
                </h3>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handlePrint(viewModal.proformaInvoice)}
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors"
                  >
                    <svg 
                      className="h-4 w-4 mr-2" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" 
                      />
                    </svg>
                    Print Invoice
                  </button>
                  <button
                    onClick={() => setViewModal({ open: false, proformaInvoice: null })}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {viewModal.proformaInvoice && (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Invoice Number</label>
                        <p className="text-lg font-semibold text-gray-900">{viewModal.proformaInvoice.invoiceNumber}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Quotation Type</label>
                        <p className="text-sm text-gray-900">
                          {viewModal.proformaInvoice.quotationType === 'quotation-new' ? 'Quotation (new)' : 'Tally Quotation'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          getStatusColor(viewModal.proformaInvoice.status)
                        }`}>
                          {viewModal.proformaInvoice.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Financial Info */}
                  <div className="bg-green-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Sub Total</label>
                        <p className="text-lg font-semibold text-blue-600">{formatCurrency(viewModal.proformaInvoice.subTotal)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Total Amount</label>
                        <p className="text-lg font-semibold text-green-600">{formatCurrency(viewModal.proformaInvoice.totalAmount)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Advance Amount</label>
                        <p className="text-lg font-semibold text-yellow-600">{formatCurrency(viewModal.proformaInvoice.advanceAmount)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Balance Amount</label>
                        <p className="text-lg font-semibold text-red-600">{formatCurrency(viewModal.proformaInvoice.balanceAmount)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Items ({viewModal.proformaInvoice.items?.length || 0})
                    </h4>
                    {viewModal.proformaInvoice.items && viewModal.proformaInvoice.items.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reserved</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {viewModal.proformaInvoice.items.map((item, index) => (
                              <tr key={index}>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  {item.itemName || item.Name || item.skuDescription}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">{item.brandName || 'N/A'}</td>
                                <td className="px-4 py-4 text-sm text-gray-900">{item.quantity} {item.unit || 'units'}</td>
                                <td className="px-4 py-4 text-sm font-semibold text-orange-600">
                                  {item.reservedQuantity || 0} {item.unit || 'units'}
                                </td>
                                <td className="px-4 py-4 text-sm font-semibold text-green-600">
                                  {formatCurrency(item.amount || 0)}
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    item.stockStatus === 'to-be-reserved' ? 'bg-yellow-100 text-yellow-800' :
                                    item.stockStatus === 'to-be-ordered' ? 'bg-red-100 text-red-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {item.stockStatus?.replace('-', ' ') || 'To be billed'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500">No items found</p>
                    )}
                  </div>

                  {/* Remarks */}
                  {viewModal.proformaInvoice.remarks && (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Remarks</h4>
                      <p className="text-gray-900">{viewModal.proformaInvoice.remarks}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delivery Order Assignment Modal */}
      {deliveryOrderModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  Delivery Orders Management
                </h3>
                <button
                  onClick={() => {
                    setDeliveryOrderModal({ open: false, proformaInvoiceId: null, assignedOrders: [] });
                    setSelectedDeliveryOrders([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Already Assigned Orders */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Assigned Delivery Orders ({deliveryOrderModal.assignedOrders.length})
                  </h4>
                  
                  {deliveryOrderModal.assignedOrders.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {deliveryOrderModal.assignedOrders.map((order) => (
                        <div key={order._id} className="bg-white border border-green-200 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                Order #{order.orderNumber || order._id}
                              </p>
                              <p className="text-xs text-gray-600">
                                {order.recipient?.name || order.recipient?.businessName || 'Unknown Recipient'}
                              </p>
                              <p className="text-xs text-gray-500">
                                Assigned: {new Date(order.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-green-600">
                                {formatCurrency(order.totalAmount || 0)}
                              </p>
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                order.status === 'NEW' ? 'bg-blue-100 text-blue-800' : 
                                order.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <DocumentDuplicateIcon className="mx-auto h-8 w-8 text-green-400 mb-2" />
                      <p className="text-green-600">No delivery orders assigned yet</p>
                    </div>
                  )}
                </div>

                {/* Available Orders to Assign */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Available Delivery Orders ({availableDeliveryOrders.length})
                  </h4>
                  <p className="text-sm text-blue-600 mb-4">
                    Select delivery orders to assign. This will change the proforma invoice status to 'Partial' until completion.
                  </p>
                  
                  {availableDeliveryOrders.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {availableDeliveryOrders.map((order) => (
                        <div key={order._id} className="bg-white border border-blue-200 rounded-lg p-3 hover:bg-blue-25 transition-colors">
                          <div className="flex items-start space-x-3">
                            <input
                              type="checkbox"
                              id={`order-${order._id}`}
                              checked={selectedDeliveryOrders.includes(order._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDeliveryOrders([...selectedDeliveryOrders, order._id]);
                                } else {
                                  setSelectedDeliveryOrders(selectedDeliveryOrders.filter(id => id !== order._id));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                            />
                            <label htmlFor={`order-${order._id}`} className="flex-1 cursor-pointer">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    Order #{order.orderNumber || order._id}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {order.recipient?.name || order.recipient?.businessName || 'Unknown Recipient'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Created: {new Date(order.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="text-right ml-4">
                                  <p className="text-sm font-semibold text-blue-600">
                                    {formatCurrency(order.totalAmount || 0)}
                                  </p>
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    order.status === 'NEW' ? 'bg-blue-100 text-blue-800' : 
                                    order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {order.status}
                                  </span>
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <DocumentDuplicateIcon className="mx-auto h-8 w-8 text-blue-400 mb-2" />
                      <p className="text-blue-600">No available delivery orders found</p>
                    </div>
                  )}
                  
                  {availableDeliveryOrders.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-blue-600">
                          Selected: <span className="font-semibold">{selectedDeliveryOrders.length}</span> delivery order(s)
                        </p>
                        <button
                          onClick={() => setSelectedDeliveryOrders([])}
                          className="text-xs text-blue-500 hover:text-blue-700"
                        >
                          Clear Selection
                        </button>
                      </div>
                      <button
                        onClick={handleAssignDeliveryOrders}
                        disabled={selectedDeliveryOrders.length === 0 || buttonLoading['assign-delivery-orders']}
                        className={`w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                          buttonLoading['assign-delivery-orders'] ? 'button-loading' : ''
                        }`}
                      >
                        {buttonLoading['assign-delivery-orders'] ? 'Assigning...' : `Assign ${selectedDeliveryOrders.length} Order(s)`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setDeliveryOrderModal({ open: false, proformaInvoiceId: null, assignedOrders: [] });
                    setSelectedDeliveryOrders([]);
                  }}
                  className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {completionModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Complete Proforma Invoice
              </h3>
              <p className="text-gray-600 mb-4">
                Please select the last delivery order for this proforma invoice completion:
              </p>
              
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {completionModal.deliveryOrders.map((order) => (
                  <div key={order._id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id={`delivery-order-${order._id}`}
                        name="lastDeliveryOrder"
                        value={order._id}
                        checked={completionModal.selectedLastDeliveryOrderId === order._id}
                        onChange={(e) => setCompletionModal(prev => ({
                          ...prev,
                          selectedLastDeliveryOrderId: e.target.value
                        }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor={`delivery-order-${order._id}`} className="ml-3 cursor-pointer flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              DO: {order.doNumber || order.Manual_dO_Number || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-600">
                              Recipient: {order.recipient?.name || order.recipient?.businessName || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Status: {order.status} | Created: {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            order.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setCompletionModal({
                    open: false,
                    proformaInvoiceId: null,
                    deliveryOrders: [],
                    selectedLastDeliveryOrderId: null
                  })}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (completionModal.selectedLastDeliveryOrderId) {
                      completeProformaInvoice(completionModal.proformaInvoiceId, completionModal.selectedLastDeliveryOrderId);
                    } else {
                      showErrorAlert('Error', 'Please select the last delivery order.');
                    }
                  }}
                  disabled={!completionModal.selectedLastDeliveryOrderId}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Complete Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      {keyboardHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Keyboard Shortcuts</h3>
              <p className="text-gray-600 mt-1">Master these shortcuts for faster navigation</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">General</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">New Invoice</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">Ctrl+N</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Save Form</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">Ctrl+S</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Cancel/Close</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">Ctrl+W</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Search</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">Ctrl+/</kbd>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Navigation</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Previous Page</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">←</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Next Page</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">→</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Close Modal</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">Esc</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Show/Hide Help</span>
                      <kbd className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">Ctrl+?</kbd>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Tips</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Use Tab to navigate between form fields</li>
                  <li>• Most actions work with Ctrl/Cmd key combinations</li>
                  <li>• Press Esc to close any open modal or dialog</li>
                  <li>• Arrow keys work for pagination when not in a form</li>
                </ul>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end">
                <button
                  onClick={() => setKeyboardHelp(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 max-w-md w-full transform transition-all duration-500 ${
          notification.show ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}>
          <div className={`p-4 rounded-lg shadow-lg border-l-4 ${
            notification.type === 'success' ? 'bg-green-50 border-green-400' :
            notification.type === 'error' ? 'bg-red-50 border-red-400' :
            notification.type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
            'bg-blue-50 border-blue-400'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {notification.type === 'success' && (
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                )}
                {notification.type === 'error' && (
                  <XCircleIcon className="h-5 w-5 text-red-400" />
                )}
                {notification.type === 'warning' && (
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                )}
                {notification.type === 'info' && (
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' :
                  notification.type === 'error' ? 'text-red-800' :
                  notification.type === 'warning' ? 'text-yellow-800' :
                  'text-blue-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={() => setNotification({ show: false, message: '', type: 'success' })}
                    className={`inline-flex rounded-md p-1.5 transition-colors ${
                      notification.type === 'success' ? 'text-green-500 hover:bg-green-100' :
                      notification.type === 'error' ? 'text-red-500 hover:bg-red-100' :
                      notification.type === 'warning' ? 'text-yellow-500 hover:bg-yellow-100' :
                      'text-blue-500 hover:bg-blue-100'
                    }`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Loading Overlay */}
      {isAnimating && (
        <div className="fixed inset-0 bg-black bg-opacity-20 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 shadow-xl animate-pulse">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-lg font-medium text-gray-900">Processing...</span>
            </div>
          </div>
        </div>
      )}

      {/* Stock Status Update Modal */}
      {stockStatusModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Update Stock Status
                </h3>
                <button
                  onClick={() => setStockStatusModal({ open: false, proformaInvoice: null, selectedItems: [] })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Select items to update from <span className="font-semibold text-orange-600">"To be Reserved"</span> to <span className="font-semibold text-green-600">"To Be Billed"</span> status:
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    {stockStatusModal.proformaInvoice?.items
                      ?.filter(item => item.stockStatus === 'to-be-reserved')
                      ?.map((item, index) => (
                        <div key={item._id || index} className="flex items-center space-x-3 p-3 bg-white rounded border">
                          <input
                            type="checkbox"
                            id={`item-${item._id || index}`}
                            checked={stockStatusModal.selectedItems.includes(item._id)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              const itemId = item._id;
                              
                              setStockStatusModal(prev => ({
                                ...prev,
                                selectedItems: isChecked 
                                  ? [...prev.selectedItems, itemId]
                                  : prev.selectedItems.filter(id => id !== itemId)
                              }));
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label 
                            htmlFor={`item-${item._id || index}`}
                            className="flex-1 cursor-pointer"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="font-medium text-gray-900">
                                  {item.itemName || item.description || 'Unnamed Item'}
                                </span>
                                {item.specification && (
                                  <span className="text-sm text-gray-500 ml-2">
                                    - {item.specification}
                                  </span>
                                )}
                                <div className="text-sm text-gray-500">
                                  Qty: {item.quantity} {item.unit || ''}
                                  {item.rate && (
                                    <span className="ml-2">
                                      @ ₹{item.rate}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                                  To be Reserved
                                </span>
                                {item.amount && (
                                  <div className="text-sm font-medium text-gray-900 mt-1">
                                    ₹{item.amount.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))
                    }
                    
                    {stockStatusModal.proformaInvoice?.items?.filter(item => item.stockStatus === 'to-be-reserved').length === 0 && (
                      <div className="text-center py-8">
                        <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No items to update</h3>
                        <p className="mt-1 text-sm text-gray-500">All items are already in "To Be Billed" status or other statuses.</p>
                      </div>
                    )}
                  </div>
                  
                  {stockStatusModal.proformaInvoice?.items?.filter(item => item.stockStatus === 'to-be-reserved').length > 0 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            const eligibleItems = stockStatusModal.proformaInvoice.items
                              .filter(item => item.stockStatus === 'to-be-reserved')
                              .map(item => item._id);
                            
                            setStockStatusModal(prev => ({
                              ...prev,
                              selectedItems: [...eligibleItems]
                            }));
                          }}
                          className="text-sm text-blue-600 hover:text-blue-500"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setStockStatusModal(prev => ({
                              ...prev,
                              selectedItems: []
                            }));
                          }}
                          className="text-sm text-gray-600 hover:text-gray-500"
                        >
                          Select None
                        </button>
                      </div>
                      <span className="text-sm text-gray-500">
                        {stockStatusModal.selectedItems.length} of {stockStatusModal.proformaInvoice?.items?.filter(item => item.stockStatus === 'to-be-reserved').length} items selected
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setStockStatusModal({ open: false, proformaInvoice: null, selectedItems: [] })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmStockStatusUpdate}
                  disabled={stockStatusModal.selectedItems.length === 0 || buttonLoading[`update-stock-${stockStatusModal.proformaInvoice?._id}`]}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                    stockStatusModal.selectedItems.length === 0 || buttonLoading[`update-stock-${stockStatusModal.proformaInvoice?._id}`]
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {buttonLoading[`update-stock-${stockStatusModal.proformaInvoice?._id}`] ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-4 w-4 border border-white border-t-transparent rounded-full mr-2"></div>
                      Updating...
                    </div>
                  ) : (
                    `Update ${stockStatusModal.selectedItems.length} Item${stockStatusModal.selectedItems.length !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            transform: scale(1);
            opacity: 1;
          }
          to {
            transform: scale(0.8);
            opacity: 0;
          }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0, 0, 0);
          }
          40%, 43% {
            transform: translate3d(0, -10px, 0);
          }
          70% {
            transform: translate3d(0, -5px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
        
        @keyframes highlight {
          0% {
            background-color: transparent;
          }
          50% {
            background-color: rgba(59, 130, 246, 0.1);
          }
          100% {
            background-color: transparent;
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.5s ease-out;
        }
        
        .animate-fadeOut {
          animation: fadeOut 0.5s ease-out;
        }
        
        .animate-bounce {
          animation: bounce 1s ease-in-out;
        }
        
        .animate-pulse-custom {
          animation: pulse 0.5s ease-in-out;
        }
        
        .animate-highlight {
          animation: highlight 1s ease-in-out;
        }
        
        .button-loading {
          position: relative;
          pointer-events: none;
          opacity: 0.7;
        }
        
        .button-loading::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 20px;
          margin-left: -10px;
          margin-top: -10px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        .confetti {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 9999;
        }
        
        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #f9ca24);
          animation: confetti-fall 3s linear infinite;
        }
        
        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
      </div>
    </div>
  );
};

export default ProformaInvoice;